import { resolveImageDensityAxisColumns } from './imageDensityConfig';

export const GEO_HEATMAP_AUTO_LAT = '__geo_auto_lat__';
export const GEO_HEATMAP_AUTO_LNG = '__geo_auto_lng__';

export const DEFAULT_GEO_HEATMAP = {
  latColumn: 'lat',
  lngColumn: 'lng',
  weightColumn: '',
  heatmapRadius: 25,
  heatmapBlur: 15,
  heatmapUseValue: false,
  showLegend: true,
  fitBoundsOnLoad: true,
  tooltipEnabled: false,
  tooltipColumns: [],
  popupColumns: [],
};

export function isGeoHeatmapChartType(chartType) {
  return chartType === 'geoHeatmap';
}

export function isPointDatasetChartType(chartType) {
  return chartType === 'imageDensity' || chartType === 'geoHeatmap';
}

function vizFieldsToGeoHeatmapConfig(viz = {}) {
  return {
    latColumn: viz.latField || viz.xField || 'lat',
    lngColumn: viz.lngField || viz.yField || 'lng',
    weightColumn: viz.densityWeightColumn || viz.geoWeightColumn || '',
    heatmapRadius: viz.geoHeatmapRadius != null ? viz.geoHeatmapRadius : 25,
    heatmapBlur: viz.geoHeatmapBlur != null ? viz.geoHeatmapBlur : viz.densityBlur != null ? viz.densityBlur : 15,
    heatmapUseValue: viz.geoHeatmapUseValue === true || Boolean(viz.densityWeightColumn),
    showLegend: viz.densityShowLegend !== false,
    fitBoundsOnLoad: viz.geoFitBoundsOnLoad !== false,
    tooltipEnabled: viz.densityTooltipEnabled === true,
    tooltipColumns: Array.isArray(viz.densityTooltipColumns) ? viz.densityTooltipColumns : [],
    popupColumns: Array.isArray(viz.geoPopupColumns) ? viz.geoPopupColumns : [],
  };
}

export function geoHeatmapAxisForSpec(field, autoSentinel) {
  if (!field || field === autoSentinel) return undefined;
  return field;
}

export function resolveGeoHeatmapAxisColumns(data, { latColumn, lngColumn } = {}) {
  const axes = resolveImageDensityAxisColumns(data, {
    xColumn: latColumn,
    yColumn: lngColumn,
  });
  return { latColumn: axes.xColumn, lngColumn: axes.yColumn };
}

/** Config del mapa geo: viz spec + chart.geoHeatmapConfig + columnas del chart. */
export function resolveGeoHeatmapConfig(chart, data) {
  if (!chart) return { ...DEFAULT_GEO_HEATMAP };

  const viz = chart.visualizationSpec?.visualization || {};
  const fieldKeys = chart.visualizationSpec?.fieldKeys || chart.fieldKeys || viz.fieldKeys || [];
  const fromViz = vizFieldsToGeoHeatmapConfig(viz);
  const stored = {
    ...(chart.advancedConfig?.geoHeatmapConfig || {}),
    ...(chart.geoHeatmapConfig || {}),
    ...(chart.advancedConfig?.mapConfig || {}),
    ...(chart.mapConfig || {}),
  };

  const configuredLat =
    stored.latColumn || chart.latColumn || fromViz.latColumn || fieldKeys.find((k) => /lat/i.test(k)) || 'lat';
  const configuredLng =
    stored.lngColumn || chart.lngColumn || fromViz.lngColumn || fieldKeys.find((k) => /lng|lon/i.test(k)) || 'lng';
  const axes = resolveGeoHeatmapAxisColumns(data, {
    latColumn: configuredLat,
    lngColumn: configuredLng,
  });

  return {
    ...DEFAULT_GEO_HEATMAP,
    ...fromViz,
    ...stored,
    latColumn: axes.latColumn,
    lngColumn: axes.lngColumn,
    heatmapUseValue:
      stored.heatmapUseValue != null
        ? stored.heatmapUseValue === true
        : Boolean(fromViz.weightColumn || stored.weightColumn),
    popupColumns:
      (Array.isArray(stored.popupColumns) && stored.popupColumns.length
        ? stored.popupColumns
        : fromViz.tooltipColumns) || [],
  };
}

export function geoHeatmapConfigToMapConfig(cfg = {}) {
  const c = { ...DEFAULT_GEO_HEATMAP, ...cfg };
  const popupColumns =
    (Array.isArray(c.popupColumns) && c.popupColumns.length ? c.popupColumns : c.tooltipColumns) || [];
  return {
    latColumn: c.latColumn,
    lngColumn: c.lngColumn,
    valueColumn: c.weightColumn || '',
    heatmapMode: true,
    heatmapUseValue: c.heatmapUseValue === true || Boolean(c.weightColumn),
    heatmapRadius: c.heatmapRadius,
    heatmapBlur: c.heatmapBlur,
    popupColumns,
    fitBoundsOnLoad: c.fitBoundsOnLoad !== false,
  };
}

export function geoHeatmapVizFieldsFromConfig(cfg = {}) {
  return {
    latField: cfg.latColumn,
    lngField: cfg.lngColumn,
    densityWeightColumn: cfg.weightColumn || '',
    geoHeatmapRadius: cfg.heatmapRadius != null ? cfg.heatmapRadius : 25,
    geoHeatmapBlur: cfg.heatmapBlur != null ? cfg.heatmapBlur : 15,
    geoHeatmapUseValue: cfg.heatmapUseValue === true,
    densityShowLegend: cfg.showLegend !== false,
    geoFitBoundsOnLoad: cfg.fitBoundsOnLoad !== false,
    densityTooltipEnabled: cfg.tooltipEnabled === true,
    densityTooltipColumns: Array.isArray(cfg.tooltipColumns) ? cfg.tooltipColumns : [],
    geoPopupColumns: Array.isArray(cfg.popupColumns) ? cfg.popupColumns : [],
  };
}

export function buildGeoHeatmapConfigFromViz(viz = {}, overrides = {}) {
  return {
    ...vizFieldsToGeoHeatmapConfig(viz),
    ...overrides,
  };
}

/** Migra chart legacy `map` + heatmapMode → geoHeatmap. */
export function migrateLegacyMapToGeoHeatmap(chart) {
  if (!chart || chart.type === 'geoHeatmap') return chart;
  const mapCfg = {
    ...(chart.advancedConfig?.mapConfig || {}),
    ...(chart.mapConfig || {}),
  };
  const isLegacyHeatmap = chart.type === 'map' && mapCfg.heatmapMode === true;
  if (!isLegacyHeatmap) return chart;

  const geoHeatmapConfig = buildGeoHeatmapConfigFromViz(
    {},
    {
      latColumn: mapCfg.latColumn || chart.latColumn || 'lat',
      lngColumn: mapCfg.lngColumn || chart.lngColumn || 'lng',
      weightColumn: mapCfg.valueColumn || '',
      heatmapUseValue: mapCfg.heatmapUseValue === true,
      heatmapRadius: mapCfg.heatmapRadius != null ? mapCfg.heatmapRadius : 25,
      heatmapBlur: mapCfg.heatmapBlur != null ? mapCfg.heatmapBlur : 15,
      popupColumns: Array.isArray(mapCfg.popupColumns) ? mapCfg.popupColumns : [],
      tooltipColumns: Array.isArray(mapCfg.popupColumns) ? mapCfg.popupColumns : [],
    }
  );

  return {
    ...chart,
    type: 'geoHeatmap',
    latColumn: geoHeatmapConfig.latColumn,
    lngColumn: geoHeatmapConfig.lngColumn,
    geoHeatmapConfig,
    advancedConfig: {
      ...(chart.advancedConfig || {}),
      geoHeatmapConfig,
    },
    mapConfig: undefined,
  };
}
